<template>
  <el-container class="workspace-page notifications-page">
    <el-main class="workspace-content">
      <div class="notification-cards">
        <el-card
          v-for="item in notifications"
          :key="item.id"
          class="notification-card"
          shadow="never">
          <div class="notification-card-header">
            <div class="notification-card-title">{{ item.title || $t("消息通知") }}</div>
            <div class="notification-card-time">{{ item.createdAtText }}</div>
          </div>
          <div class="notification-card-body">{{ item.description || '—' }}</div>
          <div class="notification-card-row">
            <span class="notification-card-label">{{ $t("有效期") }}</span>
            <span class="notification-card-value">{{ buildExpireText(item.expiredAt) }}</span>
          </div>
          <div class="notification-card-actions">
            <template v-if="item.type === 'ExportPPT' && item.extra && item.extra.file">
              <el-button size="mini" @click="copyLink(item)">{{ $t("复制链接") }}</el-button>
              <el-button size="mini" type="primary" @click="openFile(item)">{{ $t("打开文件") }}</el-button>
            </template>
            <template v-else-if="item.type === 'Application' && item.extra">
              <template v-if="item.extra.status === 0">
                <el-button size="mini" type="primary" @click="approveApplication(item)">{{ $t("同意") }}</el-button>
                <el-button size="mini" type="danger" @click="rejectApplication(item)">{{ $t("拒绝") }}</el-button>
              </template>
              <span v-else-if="item.extra.status === 1">{{ $t("已拒绝") }}</span>
              <span v-else-if="item.extra.status === 2">{{ $t("已通过") }}</span>
            </template>
          </div>
        </el-card>
        <div v-if="!notifications.length" class="notification-empty">{{ $t("暂无消息") }}</div>
      </div>
      <el-pagination
        :current-page="page"
        :page-size="pageSize"
        layout="prev, pager, next"
        @current-change="pageChanged"
        style="margin-top: 20px; text-align: right;"
      />
    </el-main>
  </el-container>
</template>

<script>
import { t } from "@/i18n";
import axios from 'axios';

export default {
  name: 'BrowserNotifications',
  data() {
    return {
      notifications: [],
      page: 1,
      pageSize: 20,
    };
  },
  methods: {
    getNotifications() {
      axios
        .get(process.env.VUE_APP_BASE_URL + '/notifications/me', {
          params: { pageSize: this.pageSize, page: this.page - 1 }
        })
        .then(res => {
          const records = res.data.records || res.data;
          const list = Array.isArray(records) ? records : [];
          this.notifications = this.enrichNotifications(list);
        });
    },
    enrichNotifications(list) {
      if (!Array.isArray(list)) return list;
      return list.map(item => {
        if (!item) return item;
        const createdAtText = this.formatDateTime(item.createdAt);
        return Object.assign({}, item, { createdAtText });
      });
    },
    buildExpireText(expiredAt) {
      const expireTime = this.parseDate(expiredAt);
      if (!expireTime) return t("本消息24小时后失效");
      const now = Date.now();
      const diffMs = expireTime.getTime() - now;
      if (diffMs <= 0) return t("本消息已失效");
      const hours = Math.ceil(diffMs / (60 * 60 * 1000));
      return t("本消息{p0}小时后失效", { p0: hours });
    },
    parseDate(value) {
      if (!value) return null;
      if (value instanceof Date) return value;
      if (typeof value === 'number') return new Date(value);
      const str = String(value);
      const normalized = str.includes('T') ? str : str.replace(/-/g, '/');
      const date = new Date(normalized);
      if (isNaN(date.getTime())) return null;
      return date;
    },
    formatDateTime(value) {
      const date = this.parseDate(value);
      if (!date) return value || '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },
    pageChanged(p) {
      this.page = p;
      this.getNotifications();
    },
    getFileUrl(row) {
      return `https://oss.ilean.com.cn/${row.extra.file}`;
    },
    copyLink(row) {
      const url = this.getFileUrl(row);
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(() => {
          this.$message.success(t("链接已复制到剪贴板"));
        });
      }
    },
    openFile(row) {
      window.open(this.getFileUrl(row), '_blank');
    },
    approveApplication(row) {
      axios
        .post(
          `${process.env.VUE_APP_BASE_URL}/company-applications/${row.id}/approve`
        )
        .then(() => {
          this.$message.success(t("已同意加入企业"));
          this.getNotifications();
        });
    },
    rejectApplication(row) {
      axios
        .post(
          `${process.env.VUE_APP_BASE_URL}/company-applications/${row.id}/reject`
        )
        .then(() => {
          this.$message.success(t("已拒绝加入企业"));
          this.getNotifications();
        });
    }
  },
  created() {
    this.getNotifications();
  }
};
</script>

<style scoped>
.notification-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}
.notification-card {
  border: 1px solid var(--border-light);
}
.notification-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.notification-card-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}
.notification-card-time {
  font-size: 12px;
  color: var(--text-muted);
}
.notification-card-body {
  font-size: 14px;
  color: var(--text-regular);
  margin-bottom: 8px;
  line-height: 1.5;
}
.notification-card-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: var(--text-muted);
}
.notification-card-value {
  color: var(--text-primary);
  margin-left: 8px;
}
.notification-card-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-top: 10px;
}
.notification-empty {
  text-align: center;
  color: #909399;
  font-size: 13px;
  padding: 16px 0 8px;
}
@media (max-width: 768px) {
  .notification-cards {
    grid-template-columns: 1fr;
  }

  .notification-card-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
  }

  .notification-card-actions .el-button {
    flex: 1;
  }
}
</style>
